import { type Component, createSignal, createMemo, For, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { Icon } from '@iconify-icon/solid'
import { cn } from '../../../shared/utils/cn'

interface TimezoneOption {
  readonly id: string
  readonly offset: string
  readonly offsetMinutes: number
  readonly label: string
  readonly region: string
}

/** Compute the current UTC offset for an IANA timezone (DST-aware). */
const getOffset = (tzId: string, now: Date): { formatted: string; minutes: number } => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tzId,
      timeZoneName: 'shortOffset'
    }).formatToParts(now)
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    if (raw === 'GMT') return { formatted: 'UTC+00:00', minutes: 0 }
    const m = raw.match(/GMT([+-])(\d+)(?::(\d+))?/)
    if (!m) return { formatted: raw, minutes: 0 }
    const sign = m[1] === '+' ? 1 : -1
    const h = Number(m[2])
    const mins = Number(m[3] ?? 0)
    return {
      formatted: `UTC${m[1]}${String(h).padStart(2, '0')}:${String(mins).padStart(2, '0')}`,
      minutes: sign * (h * 60 + mins)
    }
  } catch {
    return { formatted: '', minutes: 0 }
  }
}

/** Derive a human-friendly label from an IANA ID: "America/New_York" → "New York" */
const cityFromId = (id: string): string => {
  const last = id.split('/').pop() ?? id
  return last.replace(/_/g, ' ')
}

/** Derive region grouping from the first segment of the IANA ID. */
const regionFromId = (id: string): string => {
  const prefix = id.split('/')[0]
  const map: Record<string, string> = {
    America: 'Americas',
    US: 'Americas',
    Canada: 'Americas',
    Pacific: 'Pacific',
    Atlantic: 'Atlantic',
    Europe: 'Europe',
    Africa: 'Africa',
    Asia: 'Asia',
    Indian: 'Indian Ocean',
    Australia: 'Oceania',
    Antarctica: 'Antarctica'
  }
  return map[prefix] ?? prefix
}

/**
 * Build the full timezone list from the browser's IANA database.
 * Deduplicates by offset+city so the list stays manageable (~350 entries).
 * Sorted by offset then city name.
 */
const buildTimezones = (): TimezoneOption[] => {
  const ids = typeof Intl.supportedValuesOf === 'function' ? (Intl.supportedValuesOf('timeZone') as string[]) : []

  if (ids.length === 0) return []

  const now = new Date()
  const result: TimezoneOption[] = ids
    .filter((id) => !id.startsWith('Etc/'))
    .map((id) => {
      const { formatted, minutes } = getOffset(id, now)
      return {
        id,
        offset: formatted,
        offsetMinutes: minutes,
        label: cityFromId(id),
        region: regionFromId(id)
      }
    })

  result.sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.label.localeCompare(b.label))
  return result
}

const TIMEZONES = buildTimezones()

interface TimezoneSelectProps {
  readonly value: string
  readonly onValueChange: (id: string) => void
  readonly hasError?: boolean
  readonly placeholder?: string
}

const TimezoneSelect: Component<TimezoneSelectProps> = (props) => {
  const [open, setOpen] = createSignal(false)
  const [search, setSearch] = createSignal('')
  const [pos, setPos] = createSignal({ top: 0, left: 0, width: 0 })
  let triggerRef: HTMLButtonElement | undefined
  let inputRef: HTMLInputElement | undefined

  const selected = createMemo(() => TIMEZONES.find((tz) => tz.id === props.value))

  const filtered = createMemo(() => {
    const q = search().toLowerCase().trim()
    if (!q) return TIMEZONES
    return TIMEZONES.filter(
      (tz) =>
        tz.id.toLowerCase().includes(q) ||
        tz.label.toLowerCase().includes(q) ||
        tz.offset.includes(q) ||
        tz.region.toLowerCase().includes(q)
    )
  })

  const grouped = createMemo(() => {
    const groups: Record<string, TimezoneOption[]> = {}
    for (const tz of filtered()) {
      if (!groups[tz.region]) groups[tz.region] = []
      groups[tz.region].push(tz)
    }
    return groups
  })

  const select = (tz: TimezoneOption): void => {
    props.onValueChange(tz.id)
    setOpen(false)
    setSearch('')
  }

  const handleOpen = (): void => {
    if (triggerRef) {
      const rect = triggerRef.getBoundingClientRect()
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
    setOpen(true)
    setSearch('')
    setTimeout(() => inputRef?.focus(), 50)
  }

  const inputBase =
    'w-full h-9 px-3 py-2 bg-white rounded-md border text-sm cursor-pointer text-left flex items-center gap-2 focus:ring-1'
  const triggerClass = cn(
    inputBase,
    props.hasError
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-200 focus:ring-primary-500 focus:border-primary-500'
  )

  return (
    <div class="relative">
      {/* Trigger */}
      <button ref={triggerRef} type="button" class={triggerClass} onClick={handleOpen}>
        <Icon icon="heroicons:globe-alt" class="text-[1rem] text-gray-400 shrink-0" />
        <Show
          when={selected()}
          fallback={<span class="text-gray-400 flex-1">{props.placeholder ?? 'Select timezone'}</span>}
        >
          <span class="flex-1 text-gray-900 truncate">
            ({selected()!.offset}) {selected()!.label}
          </span>
        </Show>
        <Icon icon="heroicons:chevron-down" class="text-[1rem] text-gray-400 shrink-0" />
      </button>

      {/* Backdrop + Dropdown — rendered via Portal so the menu escapes overflow:hidden ancestors. */}
      <Show when={open()}>
        <Portal>
          <div
            class="fixed inset-0 z-40"
            onClick={() => {
              setOpen(false)
              setSearch('')
            }}
          />
          <div
            class="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden animate-menu-enter"
            style={{
              top: `${pos().top}px`,
              left: `${pos().left}px`,
              width: `${Math.max(pos().width, 384)}px`
            }}
          >
            {/* Search */}
            <div class="p-2 border-b border-gray-100">
              <div class="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
                <Icon icon="heroicons:magnifying-glass" class="text-[1rem] text-gray-400 shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search()}
                  onInput={(e) => setSearch(e.currentTarget.value)}
                  placeholder="Search timezone, city, or offset (e.g. +05)..."
                  class="flex-1 bg-transparent border-0 p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
                />
              </div>
            </div>

            {/* Results */}
            <div class="max-h-72 overflow-y-auto">
              <For each={Object.entries(grouped())}>
                {([region, tzs]) => (
                  <div>
                    <div class="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider sticky top-0 bg-white">
                      {region}
                    </div>
                    <For each={tzs}>
                      {(tz) => (
                        <button
                          type="button"
                          class="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors duration-100"
                          classList={{ 'bg-primary-50': props.value === tz.id }}
                          onClick={() => select(tz)}
                        >
                          <span class="text-xs font-mono text-gray-500 w-20 shrink-0">{tz.offset}</span>
                          <div class="flex-1 min-w-0">
                            <span class="text-sm font-medium text-gray-900">{tz.label}</span>
                            <span class="text-xs text-gray-400 ml-1.5">{tz.id}</span>
                          </div>
                          <Show when={props.value === tz.id}>
                            <Icon icon="heroicons:check" class="text-[1rem] text-primary-600 shrink-0" />
                          </Show>
                        </button>
                      )}
                    </For>
                  </div>
                )}
              </For>
              <Show when={filtered().length === 0}>
                <div class="px-3 py-6 text-center text-sm text-gray-400">No timezones found</div>
              </Show>
            </div>
          </div>
        </Portal>
      </Show>
    </div>
  )
}

export default TimezoneSelect
